/**
 * Phonics Games Configuration
 * Game settings, scoring rules, and unlock requirements
 */

import { PhonicsRule } from './natural-phonics';

// ===== Scoring Rules =====
export const scoringRules = {
  soundToShape: {
    correct: 10,
    firstTryBonus: 5,
    streakBonus: 3,
    maxPerGame: 200,
  },
  quickJudgment: {
    correct: 10,
    speedBonus: 5, // Under 2 seconds
    streakBonus: 2,
    wrongPenalty: -5,
    maxPerGame: 300,
  },
  spelling: {
    correct: 20,
    noHintBonus: 10,
    hintPenalty: { level1: -3, level2: -5, level3: -10 },
    maxPerGame: 250,
  },
  ruleCompletion: {
    oneStar: 50,
    twoStars: 100,
    threeStars: 200,
    masteryBonus: 100,
  },
};

// ===== Unlock Requirements =====
export interface RuleUnlockConfig {
  prerequisiteRules: string[];
  minStars: number;
}

export const ruleUnlockRequirements: Record<string, RuleUnlockConfig> = {
  // Short Vowels - Basic (always unlocked)
  'short-a': { prerequisiteRules: [], minStars: 0 },
  'short-e': { prerequisiteRules: ['short-a'], minStars: 1 },
  'short-i': { prerequisiteRules: ['short-e'], minStars: 1 },
  'short-o': { prerequisiteRules: ['short-i'], minStars: 1 },
  'short-u': { prerequisiteRules: ['short-o'], minStars: 1 },

  // Long Vowels - Magic E
  'long-a-magic-e': { prerequisiteRules: ['short-u'], minStars: 2 },
  'long-i-magic-e': { prerequisiteRules: ['long-a-magic-e'], minStars: 1 },
  'long-o-magic-e': { prerequisiteRules: ['long-i-magic-e'], minStars: 1 },
  'long-u-magic-e': { prerequisiteRules: ['long-o-magic-e'], minStars: 1 },

  // Vowel Teams
  'ee-team': { prerequisiteRules: ['long-u-magic-e'], minStars: 2 },
  'ea-team': { prerequisiteRules: ['ee-team'], minStars: 1 },
  'ai-team': { prerequisiteRules: ['ea-team'], minStars: 1 },
  'oa-team': { prerequisiteRules: ['ai-team'], minStars: 1 },
  'oo-team': { prerequisiteRules: ['oa-team'], minStars: 1 },
  'igh-team': { prerequisiteRules: ['oo-team'], minStars: 1 },

  // Digraphs
  'sh': { prerequisiteRules: ['short-u'], minStars: 1 },
  'ch': { prerequisiteRules: ['sh'], minStars: 1 },
  'th-voiceless': { prerequisiteRules: ['ch'], minStars: 1 },
  'ng': { prerequisiteRules: ['th-voiceless'], minStars: 1 },
  'ck': { prerequisiteRules: ['ng'], minStars: 1 },

  // Blends
  'l-blends': { prerequisiteRules: ['short-u'], minStars: 1 },
  'r-blends': { prerequisiteRules: ['l-blends'], minStars: 1 },
  's-blends': { prerequisiteRules: ['r-blends'], minStars: 1 },
  'ending-blends': { prerequisiteRules: ['s-blends'], minStars: 1 },

  // Special Rules
  'silent-e': { prerequisiteRules: ['long-u-magic-e'], minStars: 1 },
  'silent-k': { prerequisiteRules: ['silent-e'], minStars: 1 },
  'silent-w': { prerequisiteRules: ['silent-k'], minStars: 1 },
  'soft-c': { prerequisiteRules: ['silent-w'], minStars: 1 },
  'soft-g': { prerequisiteRules: ['soft-c'], minStars: 1 },
  'r-controlled': { prerequisiteRules: ['soft-g'], minStars: 1 },
};

// ===== Game Types =====
export interface GameTypeInfo {
  id: string;
  name: string;
  icon: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  pointsPerQuestion: number;
  estimatedTime: string;
}

export const gameTypes: GameTypeInfo[] = [
  {
    id: 'sound-to-shape',
    name: '听音辨形',
    icon: '🎯',
    description: '听发音，选字母组合',
    difficulty: 'easy',
    pointsPerQuestion: 10,
    estimatedTime: '3-5分钟',
  },
  {
    id: 'quick-judgment',
    name: '快速判断',
    icon: '⚡',
    description: '判断单词是否符合规则',
    difficulty: 'medium',
    pointsPerQuestion: 15,
    estimatedTime: '2-3分钟',
  },
  {
    id: 'spelling',
    name: '拼读拼写',
    icon: '🔤',
    description: '听音拼出单词',
    difficulty: 'hard',
    pointsPerQuestion: 20,
    estimatedTime: '5-8分钟',
  },
];

// ===== Badge Definitions =====
export interface BadgeDefinition {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: 'mastery' | 'streak' | 'speed' | 'accuracy' | 'special';
  requirement: {
    type: 'rules-completed' | 'category-completed' | 'streak-days' | 'perfect-game' | 'total-points';
    value: number | string;
  };
}

export const phonicsBadgeDefinitions: BadgeDefinition[] = [
  // Mastery badges
  {
    id: 'first-rule',
    name: '入门者',
    icon: '🌱',
    description: '完成第一个规则学习',
    category: 'mastery',
    requirement: { type: 'rules-completed', value: 1 },
  },
  {
    id: 'five-rules',
    name: '初学者',
    icon: '📚',
    description: '完成5个规则学习',
    category: 'mastery',
    requirement: { type: 'rules-completed', value: 5 },
  },
  {
    id: 'ten-rules',
    name: '学习者',
    icon: '📖',
    description: '完成10个规则学习',
    category: 'mastery',
    requirement: { type: 'rules-completed', value: 10 },
  },
  {
    id: 'short-vowel-master',
    name: '短元音大师',
    icon: '🔤',
    description: '掌握所有短元音规则',
    category: 'mastery',
    requirement: { type: 'category-completed', value: 'short-vowels' },
  },
  {
    id: 'long-vowel-master',
    name: '长元音大师',
    icon: '📏',
    description: '掌握所有长元音规则',
    category: 'mastery',
    requirement: { type: 'category-completed', value: 'long-vowels' },
  },

  // Streak badges
  {
    id: 'streak-3',
    name: '起步',
    icon: '🔥',
    description: '连续学习3天',
    category: 'streak',
    requirement: { type: 'streak-days', value: 3 },
  },
  {
    id: 'streak-7',
    name: '周周坚持',
    icon: '📅',
    description: '连续学习7天',
    category: 'streak',
    requirement: { type: 'streak-days', value: 7 },
  },
  {
    id: 'streak-14',
    name: '两周达人',
    icon: '🌟',
    description: '连续学习14天',
    category: 'streak',
    requirement: { type: 'streak-days', value: 14 },
  },
  {
    id: 'streak-30',
    name: '月度冠军',
    icon: '👑',
    description: '连续学习30天',
    category: 'streak',
    requirement: { type: 'streak-days', value: 30 },
  },

  // Accuracy badges
  {
    id: 'perfect-game',
    name: '完美表现',
    icon: '💯',
    description: '任意游戏100%正确率',
    category: 'accuracy',
    requirement: { type: 'perfect-game', value: 1 },
  },

  // Points badges
  {
    id: 'points-500',
    name: '积少成多',
    icon: '⭐',
    description: '累计获得500分',
    category: 'mastery',
    requirement: { type: 'total-points', value: 500 },
  },
  {
    id: 'points-1000',
    name: '百分达人',
    icon: '✨',
    description: '累计获得1000分',
    category: 'mastery',
    requirement: { type: 'total-points', value: 1000 },
  },
  {
    id: 'points-2000',
    name: '积分达人',
    icon: '🌟',
    description: '累计获得2000分',
    category: 'mastery',
    requirement: { type: 'total-points', value: 2000 },
  },
];

// ===== Category Progression =====
export interface CategoryProgression {
  id: string;
  order: number;
  name: string;
  icon: string;
  requiredStarsToUnlock: number;
}

export const categoryProgression: CategoryProgression[] = [
  { id: 'short-vowels', order: 1, name: '短元音', icon: '🔤', requiredStarsToUnlock: 0 },
  { id: 'long-vowels', order: 2, name: '长元音', icon: '📏', requiredStarsToUnlock: 5 },
  { id: 'vowel-teams', order: 3, name: '元音组合', icon: '👥', requiredStarsToUnlock: 10 },
  { id: 'digraphs', order: 4, name: '辅音组合', icon: '🔗', requiredStarsToUnlock: 6 },
  { id: 'blends', order: 5, name: '辅音连缀', icon: '🎹', requiredStarsToUnlock: 8 },
  { id: 'special', order: 6, name: '特殊规则', icon: '⭐', requiredStarsToUnlock: 15 },
];

// ===== Helper Functions =====

/**
 * Get unlock config for a rule
 */
export function getUnlockConfig(ruleId: string): RuleUnlockConfig {
  return ruleUnlockRequirements[ruleId] || { prerequisiteRules: [], minStars: 0 };
}

/**
 * Get game type info by id
 */
export function getGameTypeInfo(id: string): GameTypeInfo | undefined {
  return gameTypes.find(g => g.id === id);
}

/**
 * Get badge definition by id
 */
export function getBadgeDefinition(id: string): BadgeDefinition | undefined {
  return phonicsBadgeDefinitions.find(b => b.id === id);
}

/**
 * Calculate stars from accuracy
 */
export function calculateStars(accuracy: number): 0 | 1 | 2 | 3 {
  if (accuracy >= 90) return 3;
  if (accuracy >= 70) return 2;
  if (accuracy >= 50) return 1;
  return 0;
}

/**
 * Generate quick judgment questions
 */
export function generateQuickJudgmentQuestions(
  rules: PhonicsRule[],
  count: number = 10
): Array<{
  word: string;
  pattern: string;
  ruleId: string;
  isCorrect: boolean;
  meaning: string;
}> {
  const questions: Array<{
    word: string;
    pattern: string;
    ruleId: string;
    isCorrect: boolean;
    meaning: string;
  }> = [];

  const usedWords = new Set<string>();

  while (questions.length < count && questions.length < 50) {
    // 70% correct matches, 30% incorrect
    const shouldMatch = Math.random() < 0.7;

    if (shouldMatch) {
      // Find a word that matches a rule
      for (const rule of rules) {
        for (const example of rule.examples) {
          if (!usedWords.has(example.word)) {
            questions.push({
              word: example.word,
              pattern: rule.pattern,
              ruleId: rule.id,
              isCorrect: true,
              meaning: example.meaning,
            });
            usedWords.add(example.word);
            break;
          }
        }
        if (questions.length >= count) break;
      }
    } else {
      // Find a word that doesn't match a rule
      const randomRule = rules[Math.floor(Math.random() * rules.length)];
      const otherRules = rules.filter(r => r.id !== randomRule.id);

      if (otherRules.length > 0) {
        const anotherRule = otherRules[Math.floor(Math.random() * otherRules.length)];
        for (const example of anotherRule.examples) {
          if (!usedWords.has(example.word)) {
            questions.push({
              word: example.word,
              pattern: randomRule.pattern,
              ruleId: randomRule.id,
              isCorrect: false,
              meaning: example.meaning,
            });
            usedWords.add(example.word);
            break;
          }
        }
      }
    }
  }

  // Shuffle questions
  return questions.sort(() => Math.random() - 0.5);
}
